import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    buildDramaListSort,
    type DramaListSortDirection,
    type DramaListSortField,
} from '@/pages/drama-review/utils';
import { isDramaDeleted } from '@/pages/client-banner/utils';
import { dramaList, type DramaItem, type DramaListParams } from '@/services/dramas';
import { isAbortError, RequestGuard } from '@/utils/request-guard';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

/** 纯数字且足够长，按短剧 ID 精确查；否则按标题模糊搜 */
const buildKeywordParams = (keyword: string): Pick<DramaListParams, 'id' | 'title_fuzzy'> => {
    const trimmed = keyword.trim();
    if (!trimmed) return {};
    if (/^\d{10,}$/.test(trimmed)) {
        return { id: trimmed };
    }
    return { title_fuzzy: trimmed };
};

/** 按 ID 精确查时用与列表页一致的 size，避免 size=1 在部分网关/代理下异常；并便于和页内列表请求对齐 */
const LOOKUP_PAGE_SIZE = 20;

export const fetchDramasByIds = async (
    ids: string[],
    signal?: AbortSignal,
): Promise<Map<string, DramaItem>> => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    const result = new Map<string, DramaItem>();
    if (!uniqueIds.length) return result;

    await Promise.all(
        uniqueIds.map(async (id) => {
            const { data } = await dramaList(
                { page: 1, size: LOOKUP_PAGE_SIZE, id },
                { signal },
            );
            if (data.code !== 200) {
                throw new Error(data.msg);
            }
            const matched = data.data?.find((item) => item.id === id) ?? data.data?.[0];
            if (matched?.id === id) {
                result.set(matched.id, matched);
            }
        }),
    );

    return result;
};

export const useDramaCatalog = () => {
    const [dramas, setDramas] = useState<DramaItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [keyword, setKeyword] = useState('');
    const [onlineOnly, setOnlineOnly] = useState(false);
    const [sortField, setSortField] = useState<DramaListSortField | undefined>();
    const [sortDirection, setSortDirection] = useState<DramaListSortDirection>('desc');

    const listRequestGuard = useMemo(() => new RequestGuard(), []);
    const skipSortReloadRef = useRef(true);
    const keywordRef = useRef(keyword);
    const onlineOnlyRef = useRef(onlineOnly);

    keywordRef.current = keyword;
    onlineOnlyRef.current = onlineOnly;

    const sortParam = useMemo(
        () => buildDramaListSort(sortField, sortDirection),
        [sortDirection, sortField],
    );

    const applyClientFilters = useCallback((rows: DramaItem[]) => {
        return rows.filter((drama) => !isDramaDeleted(drama));
    }, []);

    const fetchList = useCallback(
        async (
            nextPage: number,
            nextPageSize: number,
            options?: { keyword?: string; onlineOnly?: boolean },
        ) => {
            const { id, signal } = listRequestGuard.begin();
            const searchKeyword = options?.keyword ?? keywordRef.current;
            const onlyOnline = options?.onlineOnly ?? onlineOnlyRef.current;

            setLoading(true);
            try {
                const { data } = await dramaList(
                    {
                        page: nextPage,
                        size: nextPageSize,
                        ...buildKeywordParams(searchKeyword),
                        // 审核侧「已通过」对应可选上线剧；与翻页 total 对齐，避免只在当前页前端过滤
                        ...(onlyOnline ? { status: 'Approved' as const } : {}),
                        ...(sortParam ? { sort: sortParam } : {}),
                    },
                    { signal },
                );
                if (!listRequestGuard.isCurrent(id)) return;

                if (data.code !== 200) {
                    throw new Error(data.msg);
                }

                const batch = data.data ?? [];
                const meta = data.meta;
                const filtered = applyClientFilters(batch);

                setDramas(filtered);
                setPage(
                    typeof meta?.page === 'number'
                        ? meta.page || nextPage
                        : Number(meta?.page) > 0
                          ? Number(meta?.page)
                          : nextPage,
                );
                setPageSize(
                    typeof meta?.size === 'number'
                        ? meta.size || nextPageSize
                        : Number(meta?.size) > 0
                          ? Number(meta?.size)
                          : nextPageSize,
                );
                setTotal(Number(meta?.total ?? batch.length) || 0);
            } catch (error) {
                if (isAbortError(error)) return;
                throw error;
            } finally {
                if (listRequestGuard.isCurrent(id)) {
                    setLoading(false);
                }
            }
        },
        [applyClientFilters, listRequestGuard, sortParam],
    );

    const bootstrap = useCallback(
        async (options?: { initial?: boolean }) => {
            // initial 仅保留兼容：列表改为服务端翻页，首屏与刷新同一路径
            void options;
            await fetchList(1, pageSize);
        },
        [fetchList, pageSize],
    );

    const refreshAll = useCallback(async () => {
        await fetchList(page, pageSize);
    }, [fetchList, page, pageSize]);

    const handlePageChange = useCallback(
        async (nextPage: number, nextPageSize: number) => {
            await fetchList(nextPage, nextPageSize);
        },
        [fetchList],
    );

    const handleKeywordChange = useCallback(
        async (nextKeyword: string) => {
            setKeyword(nextKeyword);
            await fetchList(1, pageSize, { keyword: nextKeyword });
        },
        [fetchList, pageSize],
    );

    const handleOnlineOnlyChange = useCallback(
        async (checked: boolean) => {
            setOnlineOnly(checked);
            await fetchList(1, pageSize, { onlineOnly: checked });
        },
        [fetchList, pageSize],
    );

    const handleSortFieldChange = useCallback((field?: DramaListSortField) => {
        setSortField(field);
    }, []);

    const handleSortDirectionChange = useCallback((direction: DramaListSortDirection) => {
        setSortDirection(direction);
    }, []);

    useEffect(() => {
        if (skipSortReloadRef.current) {
            skipSortReloadRef.current = false;
            return;
        }
        void fetchList(1, pageSize);
        // 仅排序变更时回到第 1 页重拉；pageSize 由翻页控件单独处理
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortParam]);

    return {
        dramas,
        loading,
        total,
        page,
        pageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        keyword,
        onlineOnly,
        sortField,
        sortDirection,
        bootstrap,
        refreshAll,
        handlePageChange,
        handleKeywordChange,
        handleOnlineOnlyChange,
        handleSortFieldChange,
        handleSortDirectionChange,
    };
};
